import { Copy, MoreHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useDeleteDocumentMutation } from '@/query/knowledge.query';

import { getFileName } from '@/lib/utils';

import useAlertStore from '@/store/alert.store';
import type { KnowledgeBaseFile } from '@/types/knowledge.type';

type Props = {
  knowledgeBaseID: number;
  document: KnowledgeBaseFile;
};

export const DocumentRowActions = ({ knowledgeBaseID, document }: Props) => {
  const deleteDocumentMutation = useDeleteDocumentMutation();

  const handleDeleteDocument = () => {
    if (!knowledgeBaseID) return;
    deleteDocumentMutation.mutate({ kbId: knowledgeBaseID, filePath: document.file_path });
  };

  const fileName = getFileName(document.file_path);
  const { openAlert } = useAlertStore();

  return (
    <div onClick={e => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              navigator.clipboard.writeText(document.file_path);
              toast.success('Copied', {
                description: 'Document path copied to clipboard',
              });
            }}
          >
            <Copy className="h-4 w-4" />
            Copy document path
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() =>
              openAlert({
                title: 'Delete document',
                description: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
                confirmText: 'Delete',
                onConfirm: handleDeleteDocument,
                type: 'destructive',
              })
            }
          >
            <Trash2 className="h-4 w-4" />
            Delete document
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
